<template>
  <v-layout
    align-center
    justify-center
    role="region"
  >
    <v-flex
      xs11
      sm11
      md11
      md4
      lg4
    >
      <v-card
        color="secondary"
        role="region"
        aria-label="User login form"
      >
        <!-- Toolbar / Title -->
        <v-app-bar flat>
          <v-toolbar-title
            class="text-body-2"
            role="heading"
            aria-level="1"
          >
            {{ $t('components.login.title') }}
          </v-toolbar-title>

          <v-spacer />

          <!-- Sign-up shortcut -->
          <v-tooltip
            bottom
            role="tooltip"
            aria-hidden="true"
          >
            <template #activator="{ on, attrs }">
              <v-btn
                aria-label="New player? Sign up"
                color="accent"
                dark
                :to="toSignUpLink"
                v-bind="attrs"
                v-on="on"
              >
                {{ $t('components.login.buttons.singup') }}
              </v-btn>
            </template>
            <span>{{ $t('components.login.buttons.signupPopup') }}</span>
          </v-tooltip>
        </v-app-bar>

        <v-container
          fluid
          grid-list-md
        >
          <!-- Loading indicator -->
          <v-progress-linear
            v-show="isLoading"
            color="accent"
            indeterminate
            role="status"
            aria-label="Signing in, please wait"
          />

          <!-- Server / validation errors -->
          <div
            v-for="(errorsGroup, errorsGroupIndex) in filteredErrors"
            :key="errorsGroupIndex"
            aria-live="polite"
          >
            <v-alert
              v-for="error in errorsGroup"
              :key="error.id"
              :type="error.type"
              :icon="error.icon"
              role="alert"
            >
              {{ error.msg }}
            </v-alert>
          </div>

          <!-- OAuth buttons -->
          <v-layout
            justify-center
            align-center
            aria-label="Social sign-in options"
            role="group"
          >
            <v-flex
              text-center
              flex
              xs12
              sm8
              md9
            >
              <v-btn
                aria-label="Sign in with Facebook"
                icon
                color="accent"
                :href="oAuthLinks.fb"
              >
                <v-icon small>
                  fab fa-facebook-f
                </v-icon>
              </v-btn>
              <!-- Twitter disabled for now -->
              <v-btn
                aria-label="Sign in with Google"
                icon
                color="accent"
                :href="oAuthLinks.google"
              >
                <v-icon small>
                  fab fa-google
                </v-icon>
              </v-btn>
              <v-btn
                aria-label="Sign in with GitHub"
                icon
                color="accent"
                :href="oAuthLinks.github"
              >
                <v-icon small>
                  fab fa-github-alt
                </v-icon>
              </v-btn>
            </v-flex>
          </v-layout>

          <!-- Traditional login form -->
          <v-form
            role="form"
            aria-labelledby="loginFormTitle"
            @submit.prevent="validate"
          >
            <v-layout>
              <v-flex xs12>
                <v-text-field
                  v-model="form.username"
                  v-validate="'required|min:6|max:255'"
                  prepend-icon="fal fa-user"
                  color="accent"
                  autocomplete="username"
                  data-vv-as="username"
                  data-vv-name="username"
                  :error-messages="errors.collect('username')"
                  :label="'Username'"
                  name="username"
                  type="text"
                  aria-required="true"
                />
              </v-flex>
            </v-layout>

            <v-layout>
              <v-flex xs12>
                <v-text-field
                  v-model="form.password"
                  v-validate="'required|min:6|max:50'"
                  prepend-icon="fal fa-key"
                  color="accent"
                  autocomplete="current-password"
                  data-vv-as="password"
                  data-vv-name="password"
                  :error-messages="errors.collect('password')"
                  :label="'Password'"
                  name="password"
                  type="password"
                  aria-required="true"
                />
              </v-flex>
            </v-layout>

            <v-card-actions>
              <v-btn
                aria-label="Forgot password"
                small
                color="transparent"
                class="login-forgot-password-link"
                :to="toForgotPassLink"
              >
                {{ $t('components.login.buttons.resetPass') }}
              </v-btn>

              <v-spacer />

              <v-btn
                aria-label="Sign in"
                color="accent"
                type="submit"
              >
                {{ $t('components.resetPass.buttons.signin') }}
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-container>
      </v-card>
    </v-flex>
  </v-layout>
</template>

<script>
import { filterByKeys } from "@/utils/object.utils";
import blacklist from "@/constants/blacklist";
import links from "@/constants/links";
import { mapGetters } from 'vuex';

export default {
  name: "OauthLayout",
  inject: ["$validator"],
  data() {
    return {
        form: {
            username: "",
            password: ""
        },
        oAuthLinks: links.oAuth,
        isLoading: false
    }
  },
  computed: {
      ...mapGetters(['isLogged']),

      filteredErrors() {
        return filterByKeys(
            this.$validator.errors.collect(),
            blacklist.forms.login
        );
      },
      toForgotPassLink() {
        return links.routes.resetLink;
      },
      toSignUpLink() {
        return links.routes.register.replace(':referral', '');
      }
  },
  watch: {
      isLogged(newValue) {
          if (newValue) {
            this.$router.push(links.routes.profileOverview);
          }
      }
  },
  methods: {
    async validate() {
      this.isLoading = true;
      if (await this.$validator.validateAll()) {
        this.$api.login(this.form.username, this.form.password)
            .then(() => {
                this.isLoading = false;
            })
            .catch((error) => {
              this.isLoading = false;

              console.log('error', error)

              if (error.status === 500) {
                this.validate();
              }
            });
      }
    }
  }
};
</script>

<style lang="scss">
@import '~/_variables.scss';

    .login{
        background-image: url('https://gm.goz.fun/wp-content/uploads/2023/03/login-cover.png')!important;
        background-size: cover;
        background-position: center center;

        &-forgot-password-link{
            color: $accent!important;

            &:hover{
                color: lighten($accent, 10%)!important;
            }
        }
    }
</style>
